<template>
  <div class="tab-container">
    <div class="tab-header">
      <div class="d-flex flex-column">
        <span class="ap-title ap-title__main">{{ $t('userConfiguration.basicConfiguration') }}</span>
        <span class="ap-subtitle ap-subtitle__main">{{ $t('userConfiguration.generalSub') }}</span>
      </div>
    </div>
    <div class="tab-body">
      <b-container>
        <b-row>
          <b-col>
            <b-row>
              <b-col class="pl-0">
                <div class="d-flex flex-column">
                  <span class="ap-title ap-title__section">
                    <i
                      class="section-icon far fa-flag"
                      aria-hidden="true"
                    />
                    {{ $t('userConfiguration.languages') }}
                  </span>
                </div>
              </b-col>
            </b-row>
            <form autocomplete="off">
              <b-row class="language-row bottom-bordered py-2">
                <b-col class="d-flex align-items-center pl-0">
                  <div class="d-flex flex-column">
                    <span class="option">{{ $t('userConfiguration.appLanguage') }}</span>
                  </div>
                </b-col>
                <b-col
                  cols="5"
                  class="d-flex justify-content-end pr-0"
                >
                  <vt-combo-select
                    v-model="selectedUiLanguage"
                    track-by="id"
                    :options="uiLanguages"
                    :name="'selectedUiLanguage'"
                    label="name"
                  />
                </b-col>
              </b-row>
              <b-row class="language-row bottom-bordered py-2">
                <b-col class="d-flex align-items-center pl-0">
                  <div class="d-flex flex-column">
                    <span class="option">{{ $tc('printService.printerLang') }}</span>
                  </div>
                </b-col>
                <b-col
                  cols="5"
                  class="d-flex justify-content-end pr-0"
                >
                  <vt-combo-select
                    v-model="selectedReportingLanguage"
                    track-by="id"
                    :options="reportingLanguages"
                    :name="'selectedReportingLanguage'"
                    label="name"
                  />
                </b-col>
              </b-row>
            </form>
          </b-col>
        </b-row>
        <b-row
          v-if="isPathologist"
          class="mt-4"
        >
          <b-col>
            <b-row>
              <b-col class="pl-0">
                <div class="d-flex flex-column">
                  <span class="ap-title ap-title__section">
                    <i
                      class="section-icon far fa-chart-network"
                      aria-hidden="true"
                    />
                    {{ $t('userConfiguration.premiumValidation') }}
                  </span>
                </div>
              </b-col>
            </b-row>
            <b-row class="language-row bottom-bordered pt-2 pb-3">
              <b-col
                md="10"
                class="d-flex align-items-center pl-0"
              >
                <div class="d-flex flex-column">
                  <span class="option">{{ $t('userConfiguration.premiumValidationSub') }}</span>
                </div>
              </b-col>
              <b-col
                md="2"
                class="d-flex justify-content-end pr-0"
              >
                <div class="switch-btn mt-1 p-0 d-flex align-items-center">
                  <input
                    id="toggleGlobalValidation"
                    v-model="selectedCaseValidationByOtherUsers"
                    type="checkbox"
                    class="offscreen"
                    aria-hidden="true"
                  >
                  <label
                    for="toggleGlobalValidation"
                    class="switch mb-0"
                  />
                </div>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row class="mt-4">
          <b-col class="pl-0">
            <div class="d-flex flex-column">
              <span class="ap-title ap-title__section">
                <i
                  class="section-icon far fa-portrait"
                  aria-hidden="true"
                />
                {{ $t('userConfiguration.userPhoto') }}
              </span>
            </div>
            <div class="d-flex flex-column pt-2 pb-3">
              <span class="option">{{ $t('userConfiguration.userPhotoSub') }}</span>
            </div>
          </b-col>
          <b-col
            :key="refreshProfileImageKey"
            cols="5"
            class="d-flex flex-column justify-content-center pr-0"
          >
            <div class="d-flex justify-content-center mb-3">
              <img
                v-if="userLastProfileImage"
                class="card-photo"
                :src="userLastProfileImage"
                alt="user-last-profile-image"
                aria-hidden="true"
              >
              <img
                v-else
                class="card-photo blank"
                src="@/img/user-img.png"
                alt="user-profile-imagen-blank"
                aria-hidden="true"
              >
            </div>
            <div class="text-center">
              <b-form-group>
                <div>
                  <label v-if="!userLastProfileImage"><i
                    class="far fa-upload mr-2"
                    aria-hidden="true"
                  />{{ $t('component.studyRequest.addFileModal.labelFile') }}</label>
                  <label v-else><i
                    class="far fa-check green-radiant mr-2"
                    aria-hidden="true"
                  />{{ $t('configuration.modal.user.pictureSelected') }}</label>
                </div>
                <b-button
                  id="pick-avatar"
                  class="btn__outline mt-2"
                  size="sm"
                >
                  {{ $t('configuration.modal.user.browseTextButton') }}
                </b-button>
                <b-button
                  v-if="userLastProfileImage"
                  class="btn__outline mt-2 ml-1"
                  size="sm"
                  @click="removeUserProfileImage"
                >
                  <i
                    class="far fa-trash-alt mr-2"
                    aria-hidden="true"
                  />{{ $t('global.delete') }}
                </b-button>
                <avatar-cropper
                  :upload-handler="cropperHandler"
                  :labels="{ submit: $t('global.accept'), cancel: $t('global.cancel')}"
                  trigger="#pick-avatar"
                />
              </b-form-group>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </div>
    <div class="tab-footer">
      <vt-loading-button
        class="btn--primary btn-sm"
        :params="{ promise: saveChanges }"
      >
        <i
          class="far fa-save mr-2"
          aria-hidden="true"
        />
        {{ $t('global.save') }}
      </vt-loading-button>
    </div>
  </div>
</template>

<script>
import languageService from '@/api/resources/common/languageService'
import userInformationService from '@/api/resources/init/UserInformationResource'
import sessionStore from '@/session/sessionStore'
import AvatarCropper from 'vue-avatar-cropper'
import { userappProfileImageService } from '@/api/services/common/userapp-profile-image/common-userapp-profile-image.service'
import AttachmentResource from '@/api/resources/attachment/attachmentResource'
import { RESPONSIBILITIES } from '@/api/resources/responsibilityService'
import { loadLanguageAsync } from '@/shared/util/i18n.helper'

export default {
    name: 'GeneralUserConf',
    components: { AvatarCropper },
    props: {
        userInformation: { type: Object, required: true }
    },
    data() {
        return {
            placement: 'bottom',
            selectedUiLanguage: this.userInformation.uiLanguage,
            selectedReportingLanguage: this.userInformation.reportingLanguage,
            selectedCaseValidationByOtherUsers: this.userInformation.personnel?.allowsCaseValidationByOtherUsers,
            isHidden: true,
            uiLanguages: [],
            attachmentResource: new AttachmentResource(),
            reportingLanguages: [],
            refreshProfileImageKey: 0,
            userLastProfileImage: this.$store.getters.userLastProfileImage,
            selectedProfileImageBlob: null,
            removeUserLastProfileImage: false
        }
    },
    computed: {
        isPathologist() {
            return this.userInformation.personnel?.job.code === RESPONSIBILITIES.PATHOLOGIST
        }
    },
    async mounted() {
        const [err, uiLanguages] = await this.toCall(languageService.findAllActiveAndUiEnabled())
        const [err2, reportingLanguages] = await this.toCall(languageService.findAllActiveAndReportingEnabled())
        if (err || err2) {
            this.showErrorModalGlobal()
        } else {
            this.uiLanguages = uiLanguages
            this.reportingLanguages = reportingLanguages
        }
    },
    methods: {
        removeUserProfileImage() {
            this.removeUserLastProfileImage = true
            this.userLastProfileImage = null
            this.refreshProfileImageKey++
        },
        cropperHandler(cropper) {
            this.userLastProfileImage = cropper.getCroppedCanvas({ width: 100, height: 100 }).toDataURL()
            cropper.getCroppedCanvas({ width: 100, height: 100 }).toBlob(async blob => {
                this.selectedProfileImageBlob = blob
            })
            this.refreshProfileImageKey++
        },
        async saveChanges() {
            if (await this.$_toCallSecure(this._updateUser()) !== null) {
                if (await this.$_toCallSecure(sessionStore.update()) !== null) {
                    this.$_showToastSuccess()
                }
            }
        },
        async _updateUserLastProfileImage() {
            let base64LastProfileImage = null

            const response = await this.$_toCallSecure(
                userappProfileImageService.getUserProfileImage(),
                error => this.$_handleAxiosError(error, [404]))
            if (response) {
                base64LastProfileImage = await this.attachmentResource.fileToBase64(response)
            }

            this.$store.commit('updateUserLastProfileImage', base64LastProfileImage)
        },
        async _reloadUserProfileImage() {
            let response = null
            if (this.removeUserLastProfileImage) {
                response =
                    await this.$_toCallSecure(
                        userappProfileImageService.deleteUserProfileImage())
            } else if (this.selectedProfileImageBlob) {
                response =
                    await this.$_toCallSecure(
                        userappProfileImageService.replaceUserProfileImage(this.selectedProfileImageBlob))
            }
            if (response !== null) {
                await this._updateUserLastProfileImage()
            }
        },
        async _updateUser() {
            let userData = {
                uiLanguageId: this.selectedUiLanguage.id,
                reportingLanguageId: this.selectedReportingLanguage.id
            }
            if (this.userInformation.personnel) {
                userData.personnelAllowsCaseValidationByOtherUsers = this.selectedCaseValidationByOtherUsers
            }
            const response =
                await this.$_toCallSecure(
                    userInformationService.patchUser(userData))
            if (response) {
                await sessionStore.updateUserInformation()
                await loadLanguageAsync(this.selectedUiLanguage.locale, this.selectedUiLanguage.id)
                await this._reloadUserProfileImage()
            }
        },
        clearData() {
            this.userLastProfileImage = this.$store.getters.userLastProfileImage
            this.selectedUiLanguage = this.userInformation.uiLanguage
            this.selectedReportingLanguage = this.userInformation.reportingLanguage
            this.removeUserLastProfileImage = false
        }
    }
}

</script>
<style lang="scss">
    .general-user-conf {
        height: calc(67vh - 97px);
        overflow-y: auto;
    }
</style>
<style lang="scss" scoped>
    label {
        font-size: font-size(very-small);
    }
    .card-photo {
        width: 100px;
        border-radius: 5px;
    }
    .card-photo.blank {
        opacity: .5;
    }
    .color-main i{
        color: vt-color(primary) !important;
    }

    .color-main span{
        color: vt-color(primary) !important;
    }
</style>
